import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GripHorizontal, X } from 'lucide-react';
import { CommentItem } from './CommentItem';
const POPOVER_WIDTH = 360;
const POPOVER_MAX_HEIGHT = 520;
const clampPosition = (sp) => {
    const pad = 12;
    const x = Math.min(Math.max(sp.x + 16, pad), window.innerWidth - POPOVER_WIDTH - pad);
    const y = Math.min(Math.max(sp.y - 24, pad), window.innerHeight - POPOVER_MAX_HEIGHT - pad);
    return { x, y };
};
export const CommentThreadPopover = ({ screenPosition, thread, tagSuggestions = [], currentUserId, isProjectOwner, onClose, onReply, onEdit, onDelete, onPositionChange, onSizeChange, }) => {
    const ref = useRef(null);
    // Position is seeded from the prop on first render and whenever a new pin
    // is clicked (screenPosition changes). User drags update the local state
    // and don't fight the prop — until the user clicks another pin, at which
    // point the popover jumps to the new pin.
    const [pos, setPos] = useState(() => clampPosition(screenPosition));
    useEffect(() => {
        setPos(clampPosition(screenPosition));
    }, [screenPosition.x, screenPosition.y]);
    // Mirror position out to the parent so it can draw the leader line.
    useEffect(() => {
        onPositionChange === null || onPositionChange === void 0 ? void 0 : onPositionChange(pos);
    }, [pos.x, pos.y, onPositionChange]);
    // Drag handling — header is the grab target. Track the offset of the
    // cursor relative to the popover's top-left at mousedown, then update
    // `pos` on every mousemove. Clamp to viewport on each frame.
    const [isDragging, setIsDragging] = useState(false);
    const dragOffsetRef = useRef({ x: 0, y: 0 });
    const onHeaderMouseDown = useCallback((e) => {
        // Don't start a drag if the close button (or any descendant button) was
        // the mousedown target — those have their own handlers.
        if (e.target.closest('button'))
            return;
        if (!ref.current)
            return;
        const rect = ref.current.getBoundingClientRect();
        dragOffsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        setIsDragging(true);
        e.preventDefault();
    }, []);
    useEffect(() => {
        if (!isDragging)
            return;
        const onMove = (e) => {
            const pad = 12;
            const nextX = Math.min(Math.max(e.clientX - dragOffsetRef.current.x, pad), window.innerWidth - POPOVER_WIDTH - pad);
            const nextY = Math.min(Math.max(e.clientY - dragOffsetRef.current.y, pad), window.innerHeight - 80);
            setPos({ x: nextX, y: nextY });
        };
        const onUp = () => setIsDragging(false);
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [isDragging]);
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);
    // Report the popover's actual rendered size whenever it changes (replies
    // expand the thread, edit mode swaps for a taller composer, etc.). The
    // parent uses this to feed PopoverLeaderLine the real bbox so the
    // closest-point attach doesn't land in empty space below a short popover.
    useEffect(() => {
        if (!onSizeChange || !ref.current)
            return;
        const el = ref.current;
        const observer = new ResizeObserver(() => {
            onSizeChange({ width: el.clientWidth, height: el.clientHeight });
        });
        observer.observe(el);
        // Emit once synchronously so the leader line gets a real size on the
        // first frame instead of waiting for an unrelated layout change.
        onSizeChange({ width: el.clientWidth, height: el.clientHeight });
        return () => observer.disconnect();
    }, [onSizeChange]);
    // Edit + Delete actions live on the per-comment ⋯ inside the rendered
    // CommentItem below, so the header stays minimal (drag handle + close).
    return (_jsxs("div", { ref: ref, className: "fixed rounded-lg shadow-xl flex flex-col", style: {
            left: pos.x,
            top: pos.y,
            width: POPOVER_WIDTH,
            maxHeight: POPOVER_MAX_HEIGHT,
            backgroundColor: 'var(--charcoal)',
            border: '1px solid var(--pewter)',
            zIndex: 1200,
            userSelect: isDragging ? 'none' : 'auto',
        }, onMouseDown: (e) => e.stopPropagation(), onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "flex items-center justify-between px-3 py-2 border-b", style: {
                    borderColor: 'var(--pewter)',
                    cursor: isDragging ? 'grabbing' : 'grab',
                }, onMouseDown: onHeaderMouseDown, children: [_jsxs("div", { className: "flex items-center gap-2 flex-1 min-w-0", children: [_jsx(GripHorizontal, { size: 14, style: { color: 'var(--dew)', opacity: 0.5, flexShrink: 0 } }), _jsx("span", { className: "text-xs font-semibold truncate", style: { color: 'var(--sand)' }, children: "Comment" })] }), _jsx("button", { onClick: onClose, className: "p-1 rounded hover:bg-white/10 transition-colors", style: { color: 'var(--dew)' }, title: "Close", children: _jsx(X, { size: 14 }) })] }), _jsx("div", { className: "flex-1 overflow-y-auto", children: _jsx("div", { className: "p-2", children: _jsx(CommentItem, { comment: thread, currentUserId: currentUserId, isProjectOwner: isProjectOwner, onReply: onReply, onEdit: onEdit, onDelete: onDelete, tagSuggestions: tagSuggestions, hideFlyTo: true }) }) })] }));
};
